import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import {
  Wallet,
  Download,
  Calendar,
  AlertCircle,
  Search,
  PlusCircle,
  CheckCircle2,
  MinusCircle,
  Coins,
  FileText,
  Banknote,
  Save
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

interface OverviewRow {
  paymentId: string | null;
  student: { _id: string; name: string; email: string; avatar?: string };
  class: { _id: string; name: string; tuition: number } | null;
  month: number;
  year: number;
  monthLabel: string;
  amountDue: number;
  amountPaid: number;
  balance: number;
  status: string;
  paymentMethod: string;
  paidAt: string | null;
  dueDate: string | null;
  reference: string;
  notes: string;
}

interface OverviewResponse {
  summary: {
    totalStudents: number;
    totalDue: number;
    totalPaid: number;
    totalBalance: number;
    paidCount: number;
    partialCount: number;
    unpaidCount: number;
    exemptedCount: number;
    month: number;
    year: number;
    monthLabel: string;
  };
  classes: { _id: string; name: string; tuition: number }[];
  payments: OverviewRow[];
}

interface Draft {
  amount: number;
  method: string;
  date: string;
  reference: string;
  notes: string;
  saving: boolean;
}

const STATUS_DEFS: Record<string, { label: string; color: string }> = {
  paid: { label: 'Payé', color: 'bg-green-100 text-green-800' },
  partial: { label: 'Partiel', color: 'bg-amber-100 text-amber-800' },
  unpaid: { label: 'Impayé', color: 'bg-red-100 text-red-700' },
  exempted: { label: 'Exonéré', color: 'bg-gray-100 text-gray-700' },
  voided: { label: 'Annulé', color: 'bg-gray-100 text-gray-500' }
};

const METHOD_OPTIONS = [
  { value: 'cash', label: 'Espèces' },
  { value: 'bank_transfer', label: 'Virement' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'check', label: 'Chèque' },
  { value: 'other', label: 'Autre' }
];

const METHOD_LABELS: Record<string, string> = Object.fromEntries(METHOD_OPTIONS.map((o) => [o.value, o.label]));

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount) + ' €';

const todayISO = () => new Date().toISOString().slice(0, 10);

const AdminPayments: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [classId, setClassId] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  const years = Array.from({ length: 3 }, (_, i) => now.getFullYear() - 1 + i);

  const initDraft = (row: OverviewRow): Draft => ({
    amount: row.balance > 0 ? row.balance : row.amountDue,
    method: row.paymentMethod || 'cash',
    date: row.paidAt ? new Date(row.paidAt).toISOString().slice(0, 10) : todayISO(),
    reference: row.reference || '',
    notes: row.notes || '',
    saving: false
  });

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        year: String(year),
        month: String(month),
        classId,
        status: statusFilter
      });
      if (searchTerm) params.set('search', searchTerm);
      const response = await api.get(`/payments/overview?${params.toString()}`);
      setData(response.data);

      const initial: Record<string, Draft> = {};
      for (const row of response.data.payments) {
        if (row.status === 'unpaid' || row.status === 'partial') {
          initial[row.student._id] = initDraft(row);
        }
      }
      setDrafts(initial);
    } catch (error: any) {
      console.error('Error loading overview:', error);
      toast.error(error.response?.data?.message || 'Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
    }
  }, [year, month, classId, statusFilter, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => loadOverview(), 300);
    return () => clearTimeout(timer);
  }, [loadOverview]);

  const handleGenerate = async () => {
    if (!confirm(`Générer les lignes "Impayé" pour ${MONTHS_FR[month - 1]} ${year} ?`)) return;
    try {
      setGenerating(true);
      const response = await api.post('/payments/overview/generate', { month, year, classId });
      toast.success(response.data.message);
      loadOverview();
    } catch (error: any) {
      console.error('Error generating:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const recordPayment = async (row: OverviewRow) => {
    const draft = drafts[row.student._id];
    if (!draft) return;
    if (draft.amount <= 0) {
      toast.error('Le montant doit être supérieur à 0');
      return;
    }
    setSavingId(row.student._id);
    setDrafts((prev) => ({ ...prev, [row.student._id]: { ...prev[row.student._id], saving: true } }));
    try {
      const response = await api.post('/payments/overview', {
        studentId: row.student._id,
        month: row.month,
        year: row.year,
        amountPaid: draft.amount,
        paymentMethod: draft.method,
        reference: draft.reference,
        notes: draft.notes,
        paidAt: draft.date
      });
      toast.success(response.data.message);
      loadOverview();
    } catch (error: any) {
      console.error('Error saving payment:', error);
      toast.error(error.response?.data?.message || "Erreur lors de l'enregistrement du paiement");
      setSavingId(null);
    }
  };

  const payFullAmount = (row: OverviewRow) => {
    const amount = row.balance > 0 ? row.balance : row.amountDue;
    setDrafts((prev) => ({
      ...prev,
      [row.student._id]: {
        ...prev[row.student._id],
        amount,
        method: row.paymentMethod || 'cash'
      }
    }));
  };

  const handleExempt = async (row: OverviewRow) => {
    if (!confirm(`Exonérer ${row.student.name} pour ${row.monthLabel} ${row.year} ?`)) return;
    try {
      const response = await api.post('/payments/overview', {
        studentId: row.student._id,
        month: row.month,
        year: row.year,
        exempted: true,
        notes: 'Exonéré'
      });
      toast.success(response.data.message);
      loadOverview();
    } catch (error: any) {
      console.error('Error exempting:', error);
      toast.error(error.response?.data?.message || "Erreur lors de l'exonération");
    }
  };

  const handleVoid = async (row: OverviewRow) => {
    if (!row.paymentId) return;
    if (!confirm(`Annuler le paiement de ${row.student.name} pour ${row.monthLabel} ${row.year} ?`)) return;
    try {
      const response = await api.delete(`/payments/overview/${row.paymentId}`);
      toast.success(response.data.message);
      loadOverview();
    } catch (error: any) {
      console.error('Error voiding payment:', error);
      toast.error(error.response?.data?.message || "Erreur lors de l'annulation");
    }
  };

  const handleExport = () => {
    if (!data || data.payments.length === 0) {
      toast.error('Aucun paiement à exporter');
      return;
    }
    const header = ['Étudiant', 'Classe', 'Mois', 'Année', 'Montant dû', 'Montant payé', 'Reste', 'Statut', 'Méthode', 'Date', 'Référence', 'Notes'];
    const rows = data.payments.map((r) => [
      r.student.name,
      r.class?.name || 'Sans classe',
      r.monthLabel,
      r.year,
      r.amountDue,
      r.amountPaid,
      r.balance,
      STATUS_DEFS[r.status]?.label || r.status,
      METHOD_LABELS[r.paymentMethod] || r.paymentMethod || '—',
      r.paidAt ? new Date(r.paidAt).toLocaleDateString('fr-FR') : '—',
      r.reference || '—',
      r.notes || '—'
    ]);

    const csv = [header, ...rows]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paiements-${year}-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summary = data?.summary;
  const statCards = [
    { label: 'Perçu ce mois', value: summary ? formatCurrency(summary.totalPaid) : '—', icon: Coins, color: 'text-brand-600 bg-brand-100' },
    { label: 'Attendu ce mois', value: summary ? formatCurrency(summary.totalDue) : '—', icon: Wallet, color: 'text-brand-600 bg-brand-100' },
    { label: 'Reste à percevoir', value: summary ? formatCurrency(summary.totalBalance) : '—', icon: AlertCircle, color: 'text-red-600 bg-red-100' },
    { label: 'Étudiants impayés', value: summary ? String(summary.unpaidCount) : '—', icon: Banknote, color: 'text-amber-600 bg-amber-100' }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Frais de Scolarité</h1>
            <p className="text-gray-500 mt-1">Saisie rapide des paiements mensuels par étudiant</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              {generating ? 'Génération...' : 'Générer le mois'}
            </button>
            <button
              onClick={handleExport}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter CSV
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg ${card.color.split(' ')[1]}`}>
                    <Icon className={`h-6 w-6 ${card.color.split(' ')[0]}`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{card.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-brand-600" />
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                {MONTHS_FR.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un étudiant (nom, email)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            <select
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="all">Toutes les classes</option>
              {data?.classes.map((c) => (
                <option key={c._id} value={c._id}>{c.name} ({formatCurrency(c.tuition)}/mois)</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="unpaid">Impayés</option>
              <option value="partial">Partiels</option>
              <option value="paid">Payés</option>
              <option value="exempted">Exonérés</option>
              <option value="voided">Annulés</option>
            </select>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 text-sm text-gray-600">
            <button onClick={() => setStatusFilter('all')} className="px-3 py-1 rounded-full bg-brand-100 text-brand-800">
              {summary?.totalStudents ?? 0} étudiants
            </button>
            <button onClick={() => setStatusFilter('unpaid')} className="px-3 py-1 rounded-full bg-red-100 text-red-800">
              {summary?.unpaidCount ?? 0} impayés
            </button>
            <button onClick={() => setStatusFilter('partial')} className="px-3 py-1 rounded-full bg-amber-100 text-amber-800">
              {summary?.partialCount ?? 0} partiels
            </button>
            <button onClick={() => setStatusFilter('paid')} className="px-3 py-1 rounded-full bg-green-100 text-green-800">
              {summary?.paidCount ?? 0} payés
            </button>
            <button onClick={() => setStatusFilter('exempted')} className="px-3 py-1 rounded-full bg-gray-100 text-gray-700">
              {summary?.exemptedCount ?? 0} exonérés
            </button>
          </div>
        </div>

        {/* Payments Table with inline entry */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Étudiant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classe</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Dû</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Payé</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reste</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Saisie</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.payments.map((row) => {
                  const status = STATUS_DEFS[row.status] || STATUS_DEFS.unpaid;
                  const draft = drafts[row.student._id];
                  const editable = row.status === 'unpaid' || row.status === 'partial';

                  return (
                    <React.Fragment key={row.student._id}>
                      {/* Main row */}
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-9 w-9 bg-brand-100 rounded-full flex items-center justify-center">
                              <span className="font-medium text-brand-700 text-sm">
                                {row.student.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">{row.student.name}</div>
                              <div className="text-sm text-gray-500">{row.student.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {row.class ? (
                            <>
                              <div className="text-sm text-gray-900">{row.class.name}</div>
                              <div className="text-xs text-gray-500">{formatCurrency(row.class.tuition)}/mois</div>
                            </>
                          ) : (
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded-full">Sans classe</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">{formatCurrency(row.amountDue)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-green-700">
                          {row.amountPaid > 0 ? formatCurrency(row.amountPaid) : '—'}
                          {row.paymentMethod && (
                            <div className="text-xs font-normal text-gray-500">{METHOD_LABELS[row.paymentMethod] || row.paymentMethod}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-red-700">
                          {row.balance > 0 ? formatCurrency(row.balance) : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>{status.label}</span>
                          {(editable || row.status === 'exempted') && (
                            <div className="mt-1 flex space-x-2">
                              {editable && (
                                <button
                                  onClick={() => handleExempt(row)}
                                  className="text-xs text-gray-500 hover:text-gray-700"
                                >
                                  Exonérer
                                </button>
                              )}
                              {row.paymentId && row.status !== 'voided' && (
                                <button
                                  onClick={() => handleVoid(row)}
                                  className="text-xs text-red-500 hover:text-red-700"
                                >
                                  Annuler
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-3">
                          {editable && draft ? (
                            <div className="flex flex-wrap items-end gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
                              <div>
                                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Montant (€)</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={draft.amount}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                      [row.student._id]: { ...prev[row.student._id], amount: parseFloat(e.target.value) || 0 }
                                    }))
                                  }
                                  className="w-24 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Méthode</label>
                                <select
                                  value={draft.method}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                      [row.student._id]: { ...prev[row.student._id], method: e.target.value }
                                    }))
                                  }
                                  className="px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                >
                                  {METHOD_OPTIONS.map((m) => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Date</label>
                                <input
                                  type="date"
                                  value={draft.date}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                      [row.student._id]: { ...prev[row.student._id], date: e.target.value }
                                    }))
                                  }
                                  className="px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Réf.</label>
                                <input
                                  type="text"
                                  value={draft.reference}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                      [row.student._id]: { ...prev[row.student._id], reference: e.target.value }
                                    }))
                                  }
                                  placeholder="N° reçu"
                                  className="w-24 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                />
                              </div>
                              <button
                                onClick={() => recordPayment(row)}
                                disabled={savingId === row.student._id || draft.saving}
                                className="flex items-center px-3 py-1.5 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700 transition-colors disabled:opacity-50"
                              >
                                {savingId === row.student._id ? (
                                  <span className="flex items-center">
                                    <span className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                                    ...
                                  </span>
                                ) : (
                                  <><Save className="h-4 w-4 mr-1" /> Encaisser</>
                                )}
                              </button>
                              <button
                                onClick={() => payFullAmount(row)}
                                className="px-2 py-1.5 text-xs text-brand-700 bg-brand-50 border border-brand-200 rounded-md hover:bg-brand-100"
                                title="Remplir le montant complet"
                              >
                                Complet
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center text-sm text-gray-400">
                              {(row.status === 'exempted' || row.status === 'voided') && row.notes && (
                                <span className="text-xs text-gray-500">{row.notes}</span>
                              )}
                              {(row.status === 'exempted' || row.status === 'voided') || <span>—</span>}
                            </div>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto" />
              <p className="text-gray-500 mt-4">Chargement des paiements...</p>
            </div>
          )}

          {!loading && (!data || data.payments.length === 0) && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <FileText className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun étudiant trouvé</h3>
              <p className="text-gray-500">
                Aucun étudiant ne correspond à ces critères. Utilisez « Générer le mois » pour préparer la collecte.
              </p>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex flex-wrap items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center"><CheckCircle2 className="h-4 w-4 text-green-500 mr-1" /> Payé</span>
          <span className="flex items-center"><Wallet className="h-4 w-4 text-amber-500 mr-1" /> Partiel</span>
          <span className="flex items-center"><AlertCircle className="h-4 w-4 text-red-500 mr-1" /> Impayé</span>
          <span className="flex items-center"><MinusCircle className="h-4 w-4 text-gray-500 mr-1" /> Exonéré / Annulé</span>
          <span className="ml-auto">
            La saisie se fait directement dans la ligne : montant, méthode, date puis « Encaisser ».
          </span>
        </div>
      </div>
    </Layout>
  );
};

export default AdminPayments;